import { Injectable } from '@angular/core';
import {
  AbstractControlOptions,
  UntypedFormArray,
  Validators,
} from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { PledgeProductRead } from '@app/forms/subscription/definition/interface/Pledge';
import { FormConfig } from '@app/forms/subscription/definition/types/subscription';
import FormGroupArray from '@app/forms/subscription/services/FormGroupArray';
import {
  allocateAmounts,
  CURRENT_ALLOCATION_DISTRIBUTION,
  roundTo,
  toNumber,
} from '../services/versement-allocation';

@Injectable({ providedIn: 'root' })
export abstract class EaSubscriptionBuilder extends PrFormAbstract {
  generateForm(entity: PledgeProductRead & { parameters: any }) {
    const options: AbstractControlOptions = {};
    const formConfig: FormConfig<PledgeProductRead & { parameters: any }> = {
      id: [entity.id],
      type: [entity.type],
      productId: [entity.productId],
      productName: [entity.productName],
      status: [entity.status],
      parameters: this.formBuilder.group({
        versementDistribution: [
          entity?.parameters?.versementDistribution || null,
          [Validators.required],
        ],
      }),
    };

    return this.formBuilder.group(formConfig, options);
  }

  generate(entity: any, option) {
    return FormGroupArray.generate({
      entity,
      options: option,
      formBuilder: this.formBuilder,
      generateForm: this.generateForm.bind(this),
      propertyName: 'products',
    });
  }

  get products() {
    return this.form.get('products') as UntypedFormArray;
  }

  extract() {
    return this.form.value.products.map((product, index) => {
      const originalParameters = this.entity?.products?.[index]?.parameters;
      const originalDistribution = originalParameters?.versementDistribution;
      const currentDistribution = product.parameters?.versementDistribution;

      if (currentDistribution === CURRENT_ALLOCATION_DISTRIBUTION) {
        return {
          ...product,
          parameters: {
            ...product.parameters,
            supportsInvestment:
              this.buildCurrentAllocationInvestment(originalParameters),
            productPackId: null,
          },
        };
      }

      if (
        originalDistribution &&
        currentDistribution !== originalDistribution
      ) {
        return {
          ...product,
          parameters: {
            ...product.parameters,
            supportsInvestment: {
              totalAmount:
                originalParameters?.supportsInvestment?.totalAmount ?? null,
              supportRepartition: [],
            },
            productPackId: null,
          },
        };
      }

      return product;
    });
  }

  private buildCurrentAllocationInvestment(parameters: any) {
    const versementAmount = toNumber(
      parameters?.supportsInvestment?.totalAmount,
    );
    const currentLines = (
      parameters?.currentSupportRepartition?.supportRepartition || []
    ).filter(line => !!line?.isin && toNumber(line?.amount) > 0);
    const currentTotal = currentLines.reduce(
      (sum, line) => sum + toNumber(line.amount),
      0,
    );

    const percents = currentLines.map(line =>
      currentTotal > 0 ? (toNumber(line.amount) / currentTotal) * 100 : 0,
    );
    const amounts = allocateAmounts(percents, versementAmount);

    const supportRepartition = currentLines.map((line, index) => ({
      supportId: line.supportId,
      supportName: line.supportName,
      supportType: line.supportType,
      isin: line.isin,
      isr: line.isr,
      amount: amounts[index] || 0,
      percentage:
        versementAmount > 0
          ? roundTo((amounts[index] || 0) / versementAmount, 6)
          : roundTo(percents[index] / 100, 6),
      index,
      perfP3Y: line.perfP3Y,
      volat3Y: line.volat3Y,
      risk: line.risk,
    }));

    return {
      totalAmount: roundTo(versementAmount, 2),
      supportRepartition,
    };
  }
}
